import React, { useState, Fragment, useEffect } from "react";
import { createUseStyles } from "react-jss";

import {
  IonItem,
  IonLabel,
  IonIcon,
  IonList,
  IonModal,
  IonContent,
  IonThumbnail,
  IonText,
} from "@ionic/react";

import moment from "moment";

import { busOutline, cashOutline } from "ionicons/icons";

import ModalHeader from "../../../components/Ui/ModalHeader";
import NumberFormat from "../../../components/NumberFormat";

import { Car } from "../../../interfaces/car";

import useCarSearch from "../useCarSearch";
import useInfinite, { IonItemsInfinite } from "../../../hooks/useInfinite";

interface CarSelectProps {
  date: string;
  departure_location_id: string | number;
  arrival_location_id: string | number;
  hour: string | number;
  minutes: string | number;
  disabled?: boolean;
  onIonChange?: (event: any) => void;
}

interface CarSelectListProps extends CarSelectProps {
  onChange: (value: any) => void;
}

interface Vacancy {
  quantities: number;
  price: number;
  car_id: number;
  car: Car;
}

const useStyles = createUseStyles({
  brand: {
    borderRadius: "50%",
  },
});

const getColorInfo = (quantities: number) => {
  if (quantities > 9) {
    return "medium";
  }

  if (quantities < 9 && quantities > 4) {
    return "warning";
  }

  if (quantities <= 4) {
    return "danger";
  }
};

const CarSelectList: React.FC<CarSelectListProps> = ({
  date,
  departure_location_id,
  arrival_location_id,
  hour,
  minutes,
  onChange,
}) => {
  const classes = useStyles();
  const [page, setPage] = useState(1);
  const { list, hasMore, loading } = useCarSearch({
    date,
    departure_location_id,
    arrival_location_id,
    minutes,
    hour,
    page,
    limit: 20,
  });

  const { refElLast } = useInfinite({ loading, hasMore, setPage });
  return (
    <Fragment>
      <IonList style={{ overflowY: "auto" }}>
        <IonItemsInfinite
          ionItemProps={{
            button: true,
          }}
          items={list}
          onClick={onChange}
          refItem={refElLast}
          render={(row: Vacancy) => {
            return (
              <Fragment>
                <IonThumbnail slot="start">
                  <img
                    className={classes.brand}
                    src={
                      row.car.organization.image ||
                      "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAAAAACH5BAAAAAAALAAAAAABAAEAAAICTAEAOw=="
                    }
                    alt="Marca"
                  />
                </IonThumbnail>
                <IonLabel>
                  <IonText className="car-select-title">
                    <h3>{row.car.organization.name}</h3>
                  </IonText>
                  <p>
                    {row.car.board}
                    {row.car.number}
                  </p>
                  <IonText color={getColorInfo(row.quantities)}>
                    <p>Vagas disponíveis {row.quantities}</p>
                  </IonText>
                </IonLabel>
                <IonText color="success">
                  <b>
                    <NumberFormat value={row.price} />
                  </b>
                </IonText>
              </Fragment>
            );
          }}
        />
      </IonList>
    </Fragment>
  );
};

const CarSelect: React.FC<CarSelectProps> = ({
  date,
  departure_location_id,
  arrival_location_id,
  hour,
  minutes,
  disabled,
  onIonChange,
}) => {
  const [open, setOpen] = useState(false);
  const [organization, setOrganization] = useState("");
  const [price, setPrice] = useState(0);
  const onChange = (row: Vacancy) => {
    if (onIonChange) {
      onIonChange({
        detail: {
          value: row.car_id,
          name: "car_id",
        },
      });
      setOrganization(row.car.organization.name);
      setPrice(row.price);
    }
    setOpen(false);
  };

  // Fechando a modal caso o horário seja expirado
  useEffect(() => {
    setOpen(false);
  }, [disabled]);

  return (
    <Fragment>
      <IonItem button disabled={disabled} onClick={() => setOpen(true)}>
        <IonIcon className="icon" icon={busOutline} />
        <IonLabel>Van</IonLabel>
        <span className={organization ? "" : "hour-span"}>
          {organization || "Selecione a Van"}
        </span>
      </IonItem>
      {price ? (
        <IonItem disabled={disabled}>
          <IonIcon className="icon" icon={cashOutline} />
          <IonLabel>Valor</IonLabel>
          <span>
            <NumberFormat value={price} />
          </span>
        </IonItem>
      ) : null}
      <IonContent>
        <IonModal backdropDismiss={false} isOpen={open}>
          <ModalHeader
            handleClose={() => setOpen(false)}
            title={`Selecione (${moment(date).format(
              "DD/MM/YYYY"
            )} às ${hour}:${minutes})`}
          />
          <IonContent>
            <CarSelectList
              arrival_location_id={arrival_location_id}
              date={date}
              departure_location_id={departure_location_id}
              hour={hour}
              minutes={minutes}
              onChange={onChange}
            />
          </IonContent>
        </IonModal>
      </IonContent>
    </Fragment>
  );
};

export default CarSelect;
